<script lang="ts">
  import { beforeNavigate, goto } from '$app/navigation';
  import { page } from '$app/stores';

  import { Routes } from '@shared/configs';

  import type { ImgComponentProps } from '../helpers';
  import ImageAnimated from './image-animated.svelte';

  const animDurationMove = 500; // duration of movement animation
  const animDurationOverlay = 300; // duration of overlay animation

  let nextImgUrl: string;
  let prevImgUrl: string;

  let isPrevImgLoaded = false;

  let isOverlayVisible = false;
  let isAnimated = false;

  let initialImgProps: ImgComponentProps = {
    w: 0,
    h: 0,
    x: 0,
    y: 0,
  };

  let componentProps: ImgComponentProps = initialImgProps;

  type RoutePath = string | null | undefined;

  type Flow = {
    from: string;
    to: string;
  };

  const animFlows: Flow[] = [
    {
      from: Routes.Main, // not used after pagination urls update but left just in case
      to: Routes.Artwork,
    },
    {
      from: Routes.Artworks,
      to: Routes.Artwork,
    },
    {
      from: Routes.Artworks,
      to: Routes.Auction,
    },
    {
      from: Routes.Artwork,
      to: Routes.Artwork,
    },
    {
      from: Routes.Main,
      to: Routes.Auction,
    },
    {
      from: Routes.Auctions,
      to: Routes.Auction,
    },
    {
      from: Routes.Curator,
      to: Routes.Auction,
    },
    {
      from: Routes.Auction,
      to: Routes.Auction,
    },
    {
      from: Routes.Artist,
      to: Routes.Artwork,
    },
    {
      from: Routes.Artist,
      to: Routes.Auction,
    },
    {
      from: Routes.AboutUs,
      to: Routes.Artwork,
    },
    {
      from: Routes.Curator,
      to: Routes.Artwork,
    },
    {
      from: Routes.SearchArtworks,
      to: Routes.Artwork,
    },
    {
      from: Routes.SearchArtworks,
      to: Routes.Auction,
    },
    {
      from: Routes.Selection,
      to: Routes.Artwork,
    },
  ];

  const isFlowCorrect = (from: RoutePath, to: RoutePath): boolean =>
    animFlows.some((flow: Flow) => flow.from === from && flow.to === to);

  const setImgProps = (imgProps: ImgComponentProps) => {
    componentProps = imgProps;
  };
  const setAnimated = (val: boolean) => {
    isAnimated = val;
  };
  const setOverlayVisible = (val: boolean) => {
    isOverlayVisible = val;
  };

  let isNavigated = false;

  let isNavDelayed = false;

  let gotoUrl: string | undefined;

  const animationStepOne = () => {
    setOverlayVisible(true);
    setTimeout(() => {
      if (gotoUrl) {
        isNavDelayed = true;
        goto(gotoUrl);
      }
    }, animDurationOverlay); // we need to wait for overlay to be visible
  };

  const animationStepTwo = () => {
    // we can not place this in function, but did it for 1 and 2 steps for better readability
    isNavigated = true;
    isNavDelayed = false;
    setTimeout(() => {
      setInitialState();
    }, animDurationMove); // reset state only after movement animation is finished
  };

  beforeNavigate(({ from, to, type, cancel }) => {
    const isSamePage = $page.url.pathname === to?.url?.pathname;
    const isGoto = type === 'link'; // to avoid history or other nav cases that will not have anim imgs data

    if (!isSamePage && isGoto && !isNavDelayed && isFlowCorrect(from?.route?.id, to?.route?.id)) {
      //We preparing for animation: cancel navigation, save goto url
      cancel();

      gotoUrl = to?.url?.href;
    }
  });
  // TODO - mb reset state after some timeout in case if image not loaded or some other error occurred to avoid broken UI (in afterNavigate)

  $: isPrevImgLoaded = !!prevImgUrl?.length;

  const handlePrevImg = (data: CustomEvent): void => {
    setAnimated(false);
    setImgProps(data?.detail?.props);
    prevImgUrl = data?.detail?.url;
  };

  const handleNextImg = (data: CustomEvent): void => {
    if (isPrevImgLoaded) {
      setAnimated(true);
      nextImgUrl = data?.detail?.url;
      setImgProps(data?.detail?.props);
      animationStepTwo();
    }
  };

  const setInitialState = () => {
    setOverlayVisible(false);
    setTimeout(() => {
      setImgProps(initialImgProps);
      setAnimated(false);
      prevImgUrl = '';
      nextImgUrl = '';
      isNavigated = false;
    }, animDurationOverlay); // reset state only after overlay is hidden
  };
</script>

<svelte:document on:animatedImageAction="{handlePrevImg}" on:animatedImageActionNew="{handleNextImg}" />
<div
  class="{isOverlayVisible
    ? 'opacity-100'
    : 'opacity-0'} pointer-events-none fixed left-0 top-0 z-10 h-screen w-screen bg-white transition-opacity dark:bg-black"
  style="transition: opacity {animDurationOverlay}ms"
></div>

<!-- images container -->
<div class="{isNavigated ? 'absolute' : 'fixed'} pointer-events-none left-0 top-0 z-10 min-w-full">
  <ImageAnimated
    src="{prevImgUrl}"
    isShown="{!!prevImgUrl}"
    {isAnimated}
    animDuration="{animDurationMove}"
    {componentProps}
    on:onload="{animationStepOne}"
  />

  <ImageAnimated
    src="{nextImgUrl}"
    isShown="{!!nextImgUrl}"
    {isAnimated}
    animDuration="{animDurationMove}"
    {componentProps}
  />
</div>
